import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ruler, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Bentornato!" : "Account creato con successo");
      navigate("/studio");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-12">
      <div className="w-full max-w-md fade-up">
        <div className="text-center mb-8">
          <span className="grid place-items-center h-14 w-14 rounded-lg bg-[#1e5fe0]/10 border border-[#1e5fe0]/30 mx-auto mb-4">
            <Ruler className="h-7 w-7 text-[#2f74f0]" />
          </span>
          <h1 className="font-serif text-3xl font-bold text-[#2e2015]">
            {mode === "login" ? "Accedi a My Look" : "Crea il tuo account"}
          </h1>
          <p className="text-sm text-[#6f5c47] mt-2">
            {mode === "login" ? "Il tuo stylista personale ti aspetta." : "Inizia il tuo percorso di stile su misura."}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-xl border border-[#1e5fe0]/20 bg-white/80 backdrop-blur-xl p-6 sm:p-8 space-y-5"
        >
          {mode === "register" && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#4d3a2a]">Nome</Label>
              <Input
                id="name"
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Il tuo nome"
                className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 focus-visible:ring-[#1e5fe0]/40 text-[#2e2015]"
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#4d3a2a]">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="nome@email.com"
              className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 focus-visible:ring-[#1e5fe0]/40 text-[#2e2015]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#4d3a2a]">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="bg-[#dce6ed]/60 border-[#1e5fe0]/20 focus-visible:ring-[#1e5fe0]/40 text-[#2e2015]"
            />
          </div>

          <Button
            type="submit"
            data-testid="auth-submit-btn"
            disabled={loading}
            className="w-full bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold h-11"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Accedi" : "Registrati"}
          </Button>

          <p className="text-center text-sm text-[#6f5c47]">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode-btn"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#2f74f0] hover:text-[#1e5fe0] font-medium"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
